import { forwardRef, type JSX } from 'react';
import type { Key } from 'react-aria-components';
import { useNavigate } from 'react-router';
import { msg } from '@gonfalon/messages/msg';
import type { IconButtonProps } from '@launchpad-ui/components';
import { IconButton, Menu, MenuItem, MenuTrigger, Popover, Separator, Text } from '@launchpad-ui/components';
import { isString } from 'lodash';

import { EllipsisButtonType } from './types';

import styles from './styles/EllipsisButton.module.css';

const defaultButtonAriaLabel = 'More options';

type HamburgerButtonProps = {
  /**
   * Use an aria-label if you don't pass in children and don't have a visible label to associate with the HamburgerButton.
   */
  'aria-label'?: string;
  'aria-expanded'?: boolean;
  onClick?(): void;
  size: IconButtonProps['size'];
};

export const HamburgerButton = forwardRef<HTMLButtonElement, HamburgerButtonProps>(
  ({ 'aria-label': ariaLabel, 'aria-expanded': ariaExpanded, onClick, size }, ref) => (
    <IconButton
      aria-label={ariaLabel || defaultButtonAriaLabel}
      aria-expanded={ariaExpanded}
      icon="more-vert"
      onClick={onClick}
      size={size}
      ref={ref}
      variant="minimal"
    />
  ),
);

type EllipsisButtonProps = {
  /**
   * Use an aria-label if you don't pass in children and don't have a visible label to associate with the EllipsesButton.
   */
  'aria-label'?: string;
  'aria-expanded'?: boolean;
  onClick?(): void;
  testId?: string;
  className?: string;
  disabled?: boolean;
  size?: IconButtonProps['size'];
};

export const VerticalEllipsisButton = forwardRef<HTMLButtonElement, Omit<EllipsisButtonProps, 'disabled'>>(
  ({ 'aria-label': ariaLabel, 'aria-expanded': ariaExpanded, onClick, testId, className, size }, ref) => (
    <IconButton
      icon="more-vert"
      aria-label={ariaLabel || defaultButtonAriaLabel}
      aria-expanded={ariaExpanded}
      className={className}
      onClick={onClick}
      data-test-id={testId}
      ref={ref}
      size={size}
      variant="minimal"
    />
  ),
);

export const EllipsisButton = forwardRef<HTMLButtonElement, EllipsisButtonProps>(
  (
    { 'aria-label': ariaLabel, 'aria-expanded': ariaExpanded, onClick, testId, className, size, disabled = false },
    ref,
  ) => (
    <IconButton
      aria-label={ariaLabel || defaultButtonAriaLabel}
      aria-expanded={ariaExpanded}
      className={className}
      icon="more-horiz"
      onClick={onClick}
      isDisabled={disabled}
      data-test-id={testId}
      size={size}
      ref={ref}
      variant="minimal"
    />
  ),
);

type ChartAreaButtonProps = {
  /**
   * Use an aria-label if you don't pass in children and don't have a visible label to associate with the ChartAreaButton.
   */
  'aria-label'?: string;
  'aria-expanded'?: boolean;
  onClick?(): void;
  testId?: string;
  className?: string;
  disabled?: boolean;
  size: IconButtonProps['size'];
};

export const ChartAreaButton = forwardRef<HTMLButtonElement, ChartAreaButtonProps>(
  (
    { 'aria-label': ariaLabel, 'aria-expanded': ariaExpanded, onClick, testId, className, disabled = false, size },
    ref,
  ) => (
    <IconButton
      aria-label={ariaLabel || defaultButtonAriaLabel}
      aria-expanded={ariaExpanded}
      className={className}
      onClick={onClick}
      data-test-id={testId}
      isDisabled={disabled}
      size={size}
      ref={ref}
      icon="chart-area"
      variant="minimal"
    />
  ),
);

export type EllipsisButtonWithDropdownMenuItem = {
  disabled?: boolean;
  label?: string | JSX.Element;
  key?: string;
  onClick?(event?: React.MouseEvent): void;
  tooltip?: string | JSX.Element;
  reloadDocument?: boolean;
  icon?: JSX.Element;
} & EllipsisButtonWithDropdownMenuItemWithLink &
  Divider;
type Url = string | { pathname: string; search: string };
type EllipsisButtonWithDropdownMenuItemWithLink =
  | {
      useMenuItemLink: true;
      newTab?: false;
      url: Url;
    }
  | {
      useMenuItemLink: true;
      newTab: true;
      url: Url;
    }
  | {
      useMenuItemLink?: false;
      newTab: true;
      url: Url;
    }
  | {
      useMenuItemLink?: false;
      newTab?: false;
      url?: Url;
    };
type Divider =
  | { isDivider: true; label?: string | JSX.Element; key?: string }
  | { isDivider?: false; label: string | JSX.Element; key: string };

type EllipsisButtonWithDropdownProps = {
  /**
   * Use an aria-label if you don't pass in children and don't have a visible label to associate with the Dropdown Button.
   */
  'aria-label'?: string;
  type?: EllipsisButtonType;
  className?: string;
  menuItemClassName?: string;
  items: EllipsisButtonWithDropdownMenuItem[];
  onClickEllipsisButton?(): void;
  testId?: string;
  isOpen?: boolean;
  onInteraction?(): void;
  size?: IconButtonProps['size'];
  menuDisabled?: boolean;
  popoverClassName?: string;
};

export const EllipsisButtonWithDropdown = forwardRef<HTMLButtonElement, EllipsisButtonWithDropdownProps>(
  (
    {
      'aria-label': ariaLabel,
      items,
      type,
      onClickEllipsisButton,
      testId,
      className,
      menuItemClassName,
      isOpen,
      onInteraction,
      size,
      menuDisabled = false,
      popoverClassName,
    },
    ref,
  ) => {
    const navigate = useNavigate();
    const renderIconButton = () => {
      switch (type) {
        case EllipsisButtonType.VERTICAL:
          return (
            <VerticalEllipsisButton
              aria-expanded={isOpen}
              aria-label={ariaLabel}
              className={className}
              onClick={onClickEllipsisButton}
              size={size}
              testId={testId}
              ref={ref}
            />
          );
        case EllipsisButtonType.HAMBURGER:
          return (
            <HamburgerButton
              aria-expanded={isOpen}
              size={size}
              aria-label={ariaLabel}
              onClick={onClickEllipsisButton}
              ref={ref}
            />
          );
        case EllipsisButtonType.CHART:
          return (
            <ChartAreaButton
              aria-expanded={isOpen}
              aria-label={ariaLabel}
              className={className}
              size={size}
              onClick={onClickEllipsisButton}
              disabled={menuDisabled}
              testId={testId}
              ref={ref}
            />
          );
        default:
          return (
            <EllipsisButton
              aria-expanded={isOpen}
              className={className}
              size={size}
              aria-label={ariaLabel}
              onClick={onClickEllipsisButton}
              testId={testId}
              ref={ref}
            />
          );
      }
    };

    const handleSelect = async (id: Key) => {
      const selectedItem = items.find((item) => item.key === id);
      const url = isString(selectedItem?.url) ? { pathname: selectedItem.url } : selectedItem?.url;
      if (url && !selectedItem?.useMenuItemLink) {
        if (selectedItem?.reloadDocument) {
          const pageUrl = new URL(url.pathname, location.origin);
          if ('search' in url) {
            pageUrl.search = url.search;
          }
          window.location.href = pageUrl.toString();
          return;
        }

        await navigate(url);
      }
    };

    return (
      <MenuTrigger isOpen={isOpen} onOpenChange={onInteraction}>
        {renderIconButton()}
        <Popover placement="bottom end" className={popoverClassName}>
          <Menu
            aria-label={msg('ui.ellipsisButton.menuLabel', { label: ariaLabel ?? '' })}
            className={styles.menu}
            onAction={handleSelect}
          >
            {items.map((item, num) => {
              if (item.isDivider) {
                return <Separator key={num} />;
              }

              if (item.useMenuItemLink || item.newTab) {
                return (
                  <MenuItem
                    id={item.key}
                    key={item.key}
                    href={item.url}
                    target={item.newTab ? '_blank' : undefined}
                    onAction={item.onClick}
                    className={menuItemClassName}
                  >
                    {item.icon}
                    {item.label}
                  </MenuItem>
                );
              }

              return (
                <MenuItem
                  id={item.key}
                  key={item.key}
                  isDisabled={item.disabled}
                  onAction={item.onClick}
                  href={item.url}
                  className={menuItemClassName}
                >
                  <Text slot="label">
                    {item.icon}
                    {item.label}
                  </Text>
                  {item.tooltip ? <Text slot="description">{item.tooltip}</Text> : null}
                </MenuItem>
              );
            })}
          </Menu>
        </Popover>
      </MenuTrigger>
    );
  },
);

ChartAreaButton.displayName = 'ChartAreaButton';
EllipsisButton.displayName = 'EllipsisButton';
HamburgerButton.displayName = 'HamburgerButton';
VerticalEllipsisButton.displayName = 'VerticalEllipsisButton';
